<template>
  <MglMap
    ref="map"
    :access-token="accessToken"
    :map-style="style"
    :max-zoom="10"
    :min-zoom="2"
    @load="onMapLoaded"
  >
    <MglNavigationControl position="top-right" />
    <PortLocationMarkers v-if="showMarkers" />
    <FoaLayer />
    <draw-control
      v-if="showDrawTools"
      ref="draw"
    />
    <MinFuelLayer />
    <MinTimeLayer />
    <BestTceLayer />
    <BestDtaLayer />
    <ConstructedRouteLayer />
    <SelectedRouteLayer />
    <SelectedAreaLayer />
    <VoyageMeshLayer />
    <ToolbarComponent
      v-if="showWeatherOptions"
      class="tool-bar-position"
      :on-meteo-selected="onLoadWeatherTilesPressed"
      :on-reload-button-pressed="onLoadWeatherTilesPressed"
      :on-switch-changed="onShowWeatherTilesChanged"
    />
    <DraggableInfoWindow
      :show="calculationLoading && showInfoWindow"
      @close="onCloseInfoWindow"
    />
  </MglMap>
</template>

<script>
import { MglMap, MglNavigationControl } from "vue-mapbox";
import Mapbox from "mapbox-gl";
import DrawControl from "./plugins/draw_control.vue";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import MinFuelLayer from "./layers/min_fuel.vue";
import MinTimeLayer from "./layers/min_time.vue";
import FoaLayer from "./layers/foa.vue";
import BestTceLayer from "./layers/best_tce.vue";
import BestDtaLayer from "./layers/best_dta.vue";
import PortLocationMarkers from "./layers/port_markers.vue";
import ConstructedRouteLayer from "./layers/constructed_route.vue";
import SelectedRouteLayer from "./layers/selected_route.vue";
import SelectedAreaLayer from "./layers/selected_area.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import ToolbarComponent from "./components/ToolbarComponent.vue";
import DraggableInfoWindow from "../components/DraggableInfoWindow.vue";
import { windOptionToInt } from "../../../domain/weather_routing/meteo_options/enum";
import VoyageMeshLayer from "./layers/voyage_mesh.vue";
export default {
  name: "LayerMap",
  components: {
    MglMap,
    MglNavigationControl,
    MinFuelLayer,
    MinTimeLayer,
    FoaLayer,
    BestTceLayer,
    BestDtaLayer,
    PortLocationMarkers,
    DrawControl,
    ConstructedRouteLayer,
    ToolbarComponent,
    SelectedRouteLayer,
    SelectedAreaLayer,
    DraggableInfoWindow,
    VoyageMeshLayer,
  },
  data: function () {
    return {
      accessToken:
        "pk.eyJ1Ijoia3lyb2lrbyIsImEiOiJjbWI3dmhhMHkwMXJtMnJzYzR4bHZjZDY2In0.gDeHTtgat_Gv0SaKX_kTWg",
      style: "mapbox://styles/kyroiko/cmb7ywp3o00qc01qxarx3ff11",
      showDrawTools: false,
      showWeatherOptions: false,
      showMarkers: false,
      showInfoWindow: true,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      windowPosition: { x: 20, y: 20 },
    };
  },

  created() {
    this.mapbox = Mapbox;
    this.onRouteChange();
  },
  computed: {
    ...mapGetters("weatherRouting/voyageDetailsModule", ["getFoaGeoJson"]),
    ...mapGetters("weatherRouting/tilesModule", ["getUTCTimestamp"]),
    ...mapState("weatherRouting/tilesModule", ["selectedTime", "selectedDate"]),
    ...mapState("weatherRouting", ["currentVesselCoordinates"]),
    ...mapState("weatherRouting/formModule", ["calculationLoading"]),
    windowStyle() {
      return {
        left: `${this.windowPosition.x}px`,
        top: `${this.windowPosition.y}px`,
      };
    },
  },
  methods: {
    ...mapActions("weatherRouting/tilesModule", [
      "removeCurrentLayer",
      "loadTile",
    ]),
    ...mapActions("weatherRouting/formModule", ["createVoyage"]),
    async onLoadWeatherTilesPressed(option) {
      const payload = {
        map: this.$refs.map.map,
        sourceIdBase: "weather-tile-source",
        layerIdBase: "weather-tile-layer",
        meteo: windOptionToInt(option),
      };
      await this.loadTile(payload);
    },
    onShowWeatherTilesChanged(showWeather, option) {
      if (!showWeather) {
        this.removeCurrentLayer({
          map: this.$refs.map.map,
        });
      } else {
        this.onLoadWeatherTilesPressed(option);
      }
    },
    onMapLoaded: async function (event) {
      this.flyTo = event.component.actions.flyTo;
      await this.flyTo({
        center: [50.0, 50.0],
        zoom: 2,
        speed: 8,
      });
      this.draw = this.$refs.draw;
      event.map.on("draw.create", this.onCustomAreaEdited);
      event.map.on("draw.update", this.onCustomAreaEdited);
      event.map.on("draw.delete", this.onCustomAreaDeleted);
      event.map.on("draw.modechange", this.onDrawingModeChange);
      event.map.on("click", "foaPoints", this.onFeatureClick);
      event.map.on("click", "minFuelPoints", this.onFeatureClick);
      event.map.on("click", "minTimePoints", this.onFeatureClick);
      event.map.on("click", "bestTcePoints", this.onFeatureClick);
      event.map.on("click", "bestDtaPoints", this.onFeatureClick);
      event.map.on("click", "highPoints", this.onFeatureClick);
      event.map.on("click", "lowPoints", this.onFeatureClick);
    },
    async flyToCoodinates(coordinates, zoom) {
      await this.flyTo({
        center: coordinates,
        zoom: zoom,
      });
    },
    onDrawingModeChange() {
      this.draw = this.$refs.draw;
      const data = this.draw.control.getAll();
      if (this.draw.control.getMode() == "draw_polygon") {
        const pids = [];

        // ID of the added template empty feature
        const lid = data.features[data.features.length - 1].id;

        data.features.forEach((f) => {
          if (f.geometry.type === "Polygon" && f.id !== lid) {
            pids.push(f.id);
          }
        });
        this.draw.control.delete(pids);
        this.updateCustomAreaGeoJson(null);
      }
    },
    onCustomAreaEdited() {
      const getAll = this.$refs.draw.control.getAll;
      const currentAreaFeature = getAll();
      this.updateCustomAreaGeoJson(currentAreaFeature);
    },
    onCustomAreaDeleted() {
      this.updateCustomAreaGeoJson(null);
    },
    updateCustomAreaGeoJson(newValue) {
      this.$store.commit(
        "weatherRouting/createCustomAreaModule/SET_CUSTOM_AREA_GEOJSON",
        newValue,
        { root: true }
      );
    },
    onFeatureClick(event) {
      new this.mapbox.Popup({
        maxWidth: "500px",
        anchor: "top",
        closeButton: true,
        closeOnClick: true,
        className: "weather_routing_pop_up",
        offset: 10,
      })
        .setLngLat(event.features[0].geometry.coordinates)
        .setHTML(event.features[0].properties.description)
        .addTo(this.$refs.map.map);
    },
    onRouteChange() {
      const isInCreateAreaRoute =
        this.$router.currentRoute.name == "Create Area";

      const isInVoyageDetails =
        this.$router.currentRoute.name == "Voyage Details";
      this.showDrawTools = isInCreateAreaRoute;
      this.showWeatherOptions = isInVoyageDetails;

      if (!isInVoyageDetails && this.$refs.map) {
        this.removeCurrentLayer({
          map: this.$refs.map.map,
        });
      }
    },
    onCloseInfoWindow() {
      this.$store.commit("weatherRouting/formModule/CLEAR_INTERVAL");
      this.showInfoWindow = false;
    },
  },
  watch: {
    getFoaGeoJson: {
      handler(newSource) {
        const coordinates = newSource.data.features[0].geometry.coordinates;
        const midPoint = Math.floor(coordinates.length / 2);
        this.flyToCoodinates(coordinates[midPoint], 3);
      },
      deep: true,
    },
    currentVesselCoordinates: {
      handler(newCoordinates) {
        if (newCoordinates != null) {
          this.showMarkers = true;
          if (this.$route.name == "Options") {
            this.flyToCoodinates(
              [newCoordinates.Longitude, newCoordinates.Latitude],
              5
            );
          }
        }
      },
    },
    $route() {
      this.onRouteChange();
    },
    calculationLoading() {
      if (this.calculationLoading) {
        this.showInfoWindow = this.$route.name == "Weather Routing";
      }
    },
  },
};
</script>

<style>
.button {
  position: absolute;
  bottom: 95%;
  right: 40%;
}
.gradient-bar-position {
  position: absolute;
  bottom: 25px;
  right: 0%;
}
.tool-bar-position {
  position: absolute;
  top: 25px;
  left: 10px;
}

.weather_routing_pop_up {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 0;
}

.weather_routing_pop_up .mapboxgl-popup-content {
  border-radius: 8px !important;
  padding: 5px 15px !important;
}

.popup-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1976d2 !important;
  margin-bottom: 10px !important;
  padding-bottom: 8px !important;
  border-bottom: 2px solid #1976d2 !important;
}

.weather_routing_pop_up .mapboxgl-popup-close-button {
  font-size: 20px !important;
  color: #666 !important;
  padding: 5px 10px !important;
  right: 0 !important;
  top: 0 !important;
}

.weather_routing_pop_up .mapboxgl-popup-close-button:hover {
  background-color: transparent !important;
  color: #000 !important;
}

.vessel-popup-content {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif !important;
  line-height: 1.5 !important;
  color: #333 !important;
}

.popup-item {
  display: flex !important;
  align-items: center !important;
  padding: 8px 0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.popup-item:last-child {
  border-bottom: none !important;
}

.popup-item i {
  font-size: 20px !important;
  color: #1976d2 !important;
  margin-right: 12px !important;
  width: 24px !important;
  text-align: center !important;
}

.popup-item-content {
  display: flex !important;
  flex-direction: column !important;
}

.popup-label {
  font-size: 12px !important;
  color: #666 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.popup-value {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #333 !important;
  margin-top: 2px !important;
}

.weather_routing_pop_up .mapboxgl-popup-tip {
  border-top-color: rgba(255, 255, 255, 0.95) !important;
}

.map-dialog {
  position: absolute !important;
  z-index: 1000;
}

.dialog-content {
  padding: 16px;
}

.dialog-content p {
  margin: 8px 0;
  font-size: 14px;
  color: #333;
}

.v-card {
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.v-card-title {
  background-color: #1976d2;
  color: white;
  padding: 12px 16px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.v-card-title .v-btn {
  color: white;
}

.v-card-text {
  padding: 16px;
}

.map-info-window {
  position: absolute;
  width: 300px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  user-select: none;
}

.info-window-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #1976d2;
  color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  cursor: move;
}

.info-window-header span {
  font-weight: 500;
  font-size: 16px;
}

.info-window-content {
  padding: 16px;
}

.info-window-content p {
  margin: 8px 0;
  font-size: 14px;
  color: #333;
}

.info-window-header .v-btn {
  color: white;
}
</style>
